import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SettingsContainer,
  TabNavigationContainer,
  TabButton,
  TabContent,
  StylingSection,
  FontControlsContainer,
  FontLabel,
  SettingItem,
  SettingLabel,
  Content,
} from './DocumentSettingsTab.styles';
import { CollapsibleSection as CollapsibleSectionComponent } from './components/CollapsibleSection';
import { SharedFontControls } from './components/SharedFontControls';
import { AccessibilitySection } from './components/AccessibilitySection';
import { DisplayNameSection } from './components/DisplayNameSection';
import { CloneableSection } from './components/CloneableSection';
import { PpiSection } from './components/PpiSection';
import { PdfQualitySection } from './components/PdfQualitySection';
import BusinessContractSection from './components/BusinessContractSection';
import FileSettings from './FileSettings';
import { DESIGN_AREA_SETTINGS_TAB } from '../../SidebarMenu/translationValues';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../../context/complexArtboardContext';
import { useDisplayNameSettings } from './hooks/useDisplayNameSettings';

const NAMESPACES = ['DESIGN_AREA_SETTINGS_TAB'];

const DocumentSettingsTab = ({ CurrentDesign, fontListVisible, onFontToogle, ...props }) => {
  const { t } = useTranslation(NAMESPACES);
  const [activeTab, setActiveTab] = useState('general');
  const [stylingExpanded, setStylingExpanded] = useState(true);
  const [businessContractExpanded, setBusinessContractExpanded] = useState(false);
  const [tagsExpanded, setTagsExpanded] = useState(false);

  const displayNameSettings = useDisplayNameSettings();
  
  const readonlySelector = useCallback(store => store[CONTEXT_KEYS.READONLY_TEMPLATE], []);
  const [readOnly] = useArtboardContext(readonlySelector, 'DocumentSettingsTab');

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `DocumentSettingsTab`);

  const isReadOnlySelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [isEditMode] = useArtboardContext(isReadOnlySelector, `DocumentSettingsTab`);

  // Helper functions for scopeType and scopeId
  const groupIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_ID], []);
  const [groupId] = useDocumentContext(groupIdSelector, `DocumentSettingsTab`);

  const communicationIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.COMMUNICATION_ID], []);
  const [communicationId] = useDocumentContext(communicationIdSelector, `DocumentSettingsTab`);

  const getScopeType = useCallback((design) => {
    if (design === 'document') {
      return 'communication';
    } else if (design === 'flow') {
      return 'flow-group';
    } else if (design === 'block') {
      return 'block-document';
    }
    return 'flow-group';
  }, []);

  const getScopeId = useCallback((design) => {
    if (design === 'document') {
      return communicationId;
    } else {
      return groupId;
    }
  }, [groupId, communicationId]);


  const renderGeneralTab = () => (
    <TabContent>
      <CollapsibleSectionComponent
        title={t(DESIGN_AREA_SETTINGS_TAB.STYLING?.key || 'Styling', DESIGN_AREA_SETTINGS_TAB.STYLING?.value || 'Styling')}
        defaultExpanded={stylingExpanded}
        onToggle={setStylingExpanded}
      >
        <Content isEditMode={!isEditMode || isInteractiveDesign}>
        <StylingSection>
          <FontControlsContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <SharedFontControls
                CurrentDesign={CurrentDesign}
                fontListVisible={fontListVisible}
                onFontToogle={onFontToogle}
              />
            </div>
          </FontControlsContainer>
        </StylingSection>
        </Content>
      </CollapsibleSectionComponent>

      <CollapsibleSectionComponent
        title={t(DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT?.key || 'Business Contract', DESIGN_AREA_SETTINGS_TAB.BUSINESS_CONTRACT?.value || 'Business Contract')}
        defaultExpanded={businessContractExpanded}
        onToggle={setBusinessContractExpanded}
      >
        <Content isEditMode={!isEditMode || isInteractiveDesign}>
          <BusinessContractSection readOnly={readOnly} />
        </Content>
      </CollapsibleSectionComponent>

      <CollapsibleSectionComponent
        title={t(DESIGN_AREA_SETTINGS_TAB.TAGS?.key || 'Tags', DESIGN_AREA_SETTINGS_TAB.TAGS?.value || 'Tags')}
        defaultExpanded={tagsExpanded}
        onToggle={setTagsExpanded}
      >
        <Content isEditMode={!isEditMode || isInteractiveDesign}>
          {/* Tags section - FileSettings component handles tags */}
          <FileSettings 
            scopeType={getScopeType(CurrentDesign)} 
            scopeId={getScopeId(CurrentDesign)} 
          />
        </Content>
      </CollapsibleSectionComponent>
    </TabContent>
  );

  const renderAccessibilityTab = () => (
    <TabContent isEditMode={!isEditMode || isInteractiveDesign}>
      <AccessibilitySection
        CurrentDesign={CurrentDesign}
        readOnly={readOnly}
      />
    </TabContent>
  );

  const renderPdfSettingsTab = () => (
    <TabContent isEditMode={!isEditMode || isInteractiveDesign}>
      <SettingItem>
        <DisplayNameSection
          CurrentDesign={CurrentDesign}
          readOnly={readOnly}
          isEditMode={isEditMode}
        />
      </SettingItem>

      <SettingItem>
        <CloneableSection
          CurrentDesign={CurrentDesign}
          isInteractiveDesign={isInteractiveDesign}
          readOnly={readOnly}
        />
      </SettingItem>

      <SettingItem>
        <PpiSection
          CurrentDesign={CurrentDesign}
          showSlider={false}
        />
      </SettingItem>

      <SettingItem>
        <PdfQualitySection
          readOnly={readOnly}
        />
      </SettingItem>
    </TabContent>
  );


  return (
    <SettingsContainer>
      <TabNavigationContainer>
        <TabButton
          active={activeTab === 'general'}
          onClick={() => setActiveTab('general')}
        >
          {t(DESIGN_AREA_SETTINGS_TAB.GENERAL?.key || 'General', DESIGN_AREA_SETTINGS_TAB.GENERAL?.value || 'General')}
        </TabButton>
        <TabButton
          active={activeTab === 'accessibility'}
          onClick={() => setActiveTab('accessibility')}
        >
          {t(DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY?.key || 'Accessibility', DESIGN_AREA_SETTINGS_TAB.ACCESSIBILITY?.value || 'Accessibility')}
        </TabButton>
        <TabButton
          active={activeTab === 'pdf-settings'}
          onClick={() => setActiveTab('pdf-settings')}
        >
          {t(DESIGN_AREA_SETTINGS_TAB.PDF_SETTINGS?.key || 'PDF Settings', DESIGN_AREA_SETTINGS_TAB.PDF_SETTINGS?.value || 'PDF Settings')}
        </TabButton>
      </TabNavigationContainer>

      {activeTab === 'general' && renderGeneralTab()}
      {activeTab === 'accessibility' && renderAccessibilityTab()}
      {activeTab === 'pdf-settings' && renderPdfSettingsTab()}
    </SettingsContainer>
  );
};

export default DocumentSettingsTab;

